import { useCallback, useEffect, useRef, useState } from "react";

export function useCellSaveState(savedDuration = 1200) {
  const [statuses, setStatuses] = useState({});
  const queues = useRef({});
  const latestTasks = useRef({});
  const timers = useRef({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const activeTimers = timers.current;

    return () => {
      mounted.current = false;
      Object.values(activeTimers).forEach(clearTimeout);
    };
  }, []);

  const setStatus = useCallback((key, status) => {
    if (!mounted.current) return;
    setStatuses((current) => ({ ...current, [key]: status }));
  }, []);

  const saveCell = useCallback(
    (key, operation, queueKey = key) => {
      if (timers.current[key]) {
        clearTimeout(timers.current[key]);
        delete timers.current[key];
      }

      setStatus(key, "saving");

      const previous = queues.current[queueKey] ?? Promise.resolve(null);
      const task = previous
        .catch(() => null)
        .then(operation)
        .catch(() => ({ success: false }));

      queues.current[queueKey] = task;
      latestTasks.current[key] = task;

      task.then((result) => {
        if (!mounted.current) return;

        if (queues.current[queueKey] === task) {
          delete queues.current[queueKey];
        }

        if (latestTasks.current[key] !== task) return;

        delete latestTasks.current[key];
        if (!result?.success) {
          setStatus(key, "error");
          return;
        }

        setStatus(key, "saved");
        timers.current[key] = setTimeout(() => {
          if (!mounted.current) return;
          setStatuses((current) => {
            if (current[key] !== "saved") return current;

            const next = { ...current };
            delete next[key];
            return next;
          });
          delete timers.current[key];
        }, savedDuration);
      });

      return task;
    },
    [savedDuration, setStatus],
  );

  const getSaveStatus = useCallback(
    (key) => statuses[key] ?? "idle",
    [statuses],
  );

  return { getSaveStatus, saveCell };
}
